import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IResponseDetail } from '@interfaces/ICommon';
import { IEvent } from '@interfaces/IEvent';
import { IStudentWorksQParams } from '@interfaces/IQParams';
import { IStudentWork } from '@interfaces/IStudentWork';
import { IStudentWorkReview } from '@interfaces/IStudentWorkReview';
import { IStudentWorksStats } from '@interfaces/IStudentWorksStats';
import { IReviewTemplates } from '@interfaces/IReviewTemplates';
import { IUser } from '@interfaces/IUser';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class StudentWorksService {
  constructor(private _http: HttpClient) {}

  addStudentWork(work: FormData): Observable<IStudentWork> {
    const url = `${environment.baseUrl}api/v1/student-works/`;
    return this._http.post<IStudentWork>(url, work);
  }

  updateStudentWork(work: FormData, id: number): Observable<IStudentWork> {
    const url = `${environment.baseUrl}api/v1/student-works/${id}/`;
    return this._http.put<IStudentWork>(url, work);
  }

  getStudentWorks(queries?: { [key: string]: string | boolean }): Observable<{
    count: number;
    challenge_count: number;
    urgent_homeworks_count: number;
    results: IStudentWork[];
  }> {
    let url = `${environment.baseUrl}api/v1/student-works/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<{
      count: number;
      challenge_count: number;
      urgent_homeworks_count: number;
      results: IStudentWork[];
    }>(url);
  }

  getStudentWorksStats(qParams?: {
    [key: string]: string;
  }): Observable<IStudentWorksStats> {
    const url = `${environment.baseUrl}api/v1/groups/counters/`;
    return this._http.get<IStudentWorksStats>(url, { params: qParams });
  }

  getReviewTemplates() {
    const url = `${environment.baseUrl}api/v1/student-works-reviews/templates/`;
    return this._http.get<IReviewTemplates[]>(url);
  }

  getReviewTemplate(id: number, template_id: number) {
    const url = `${environment.baseUrl}api/v1/student-works-reviews/${id}/`;
    return this._http.patch(url, { template_id });
  }

  transferReviewers(reviewers: number[], to_user: number) {
    const url = `${environment.baseUrl}api/v1/student-works-reviews/transfer-reviewers/`;
    return this._http.post(url, { reviews: reviewers, to_user: to_user });
  }

  addStudentWorksReview(
    eventWorks: number[]
  ): Observable<{ eventWorks: number[]; reviewer: IUser }> {
    const url = `${environment.baseUrl}api/v1/student-works-reviews/`;
    return this._http.post<{ eventWorks: number[]; reviewer: IUser }>(url, {
      event_works: eventWorks,
    });
  }

  getStudentWorkReview(id: number): Observable<IStudentWorkReview> {
    const url = `${environment.baseUrl}api/v1/student-works-reviews/${id}/`;
    return this._http.get<IStudentWorkReview>(url);
  }

  updateStudentWorkReview(data: {
    text?: string;
    id: number;
    points?: string;
    is_published?: boolean;
  }): Observable<IStudentWorkReview> {
    const url = `${environment.baseUrl}api/v1/student-works-reviews/${data.id}/`;
    return this._http.patch<IStudentWorkReview>(url, data);
  }

  publishReviews(
    reviews: number[],
    scheduled_datetime?: string
  ): Observable<{ id: number; is_published: boolean; message: string }[]> {
    const url = `${environment.baseUrl}api/v1/student-works-reviews/publish/`;
    return this._http.patch<
      { id: number; is_published: boolean; message: string }[]
    >(url, {
      reviews,
      scheduled_datetime,
    });
  }

  downloadStudentWorks(works: number[]): Observable<IResponseDetail> {
    const url = `${environment.baseUrl}api/v1/student-works/download/`;
    return this._http.post<IResponseDetail>(url, { works });
  }

  downloadAllWorks(queries?: {
    [key: string]: string;
  }): Observable<IResponseDetail> {
    let url = `${environment.baseUrl}api/v1/student-works/download/`;

    if (queries) {
      url += `?`;

      Object.keys(queries).forEach(key => {
        if (key !== 'page') {
          if (queries[key] !== '') {
            url += `${key}=${queries[key]}&`;
          }
        }
      });
    }

    return this._http.post<IResponseDetail>(url, {});
  }

  takeInCheck(qParams: IStudentWorksQParams): Observable<IResponseDetail> {
    let params = new HttpParams();

    Object.entries(qParams).forEach(param => {
      params = params.set(param[0], param[1]);
    });

    const url = `${environment.baseUrl}api/v1/student-works/take-in-check/`;
    return this._http.post<IResponseDetail>(url, {}, { params });
  }

  autoAssignWorks(payload: { event: IEvent; reviewer_ids?: number[] }) {
    const url = `${environment.baseUrl}api/v1/student-works/autoassign/`;
    let data: { [key: string]: number | number[] | undefined } = {
      event_id: payload.event.id,
    };

    if (payload.reviewer_ids) {
      data = {
        event_id: payload.event.id,
        reviewer_ids: payload.reviewer_ids,
      };
    }

    return this._http.post(url, data);
  }
}
